import { env } from '@/lib/env';
import { clearTokens, readAccessToken, readRefreshToken, storeTokens } from '@/lib/auth/token';

/*
  API 클라이언트. 백엔드는 `ApiResponse<T>` 봉투로 답한다 — front 와 같은 규약이다.
  axios 를 쓰지 않는 것도 같다(표준 fetch).
*/

export type ApiError = {
  code: string;
  message: string;
  detail?: Record<string, string> | null;
};

type Envelope<T> = {
  ok: boolean;
  data: T | null;
  error: ApiError | null;
};

export class ApiRequestError extends Error {
  readonly status: number;
  readonly apiError: ApiError | null;

  constructor(status: number, apiError: ApiError | null) {
    super(apiError?.message ?? `요청이 실패했습니다 (${status})`);
    this.name = 'ApiRequestError';
    this.status = status;
    this.apiError = apiError;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
};

/**
 * 401 을 만나면 **한 번만** 토큰을 새로 받아 원 요청을 다시 보낸다.
 *
 * <p>여러 요청이 동시에 401 을 받아도 재발급은 한 번이어야 한다 — 각자 부르면 회전이 엇갈려
 * 뒤늦게 도착한 쪽이 이미 폐기된 refresh 를 들고 오고, 서버는 그것을 <b>재사용 공격</b>으로 보고
 * 그 계정의 토큰을 통째로 끊는다. 진행 중인 약속을 공유해 그 상황을 만들지 않는다.
 */
let refreshing: Promise<boolean> | null = null;

async function refreshOnce(): Promise<boolean> {
  if (refreshing !== null) return refreshing;

  refreshing = (async () => {
    const refreshToken = readRefreshToken();
    if (refreshToken === null) return false;

    try {
      const response = await fetch(`${env.apiBaseUrl}/auth/token/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        clearTokens();
        return false;
      }
      const envelope = (await response.json()) as Envelope<{
        accessToken: string;
        refreshToken: string | null;
      }>;
      if (!envelope.ok || envelope.data === null) {
        clearTokens();
        return false;
      }
      storeTokens(envelope.data.accessToken, envelope.data.refreshToken);
      return true;
    }
    catch {
      // 네트워크가 끊긴 것은 토큰이 죽은 것과 다르다. 지우지 않는다.
      return false;
    }
    finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

/**
 * 개발용 응답으로 갈리는 **유일한 자리**.
 *
 * <p>동적 {@code import()} 라 운영 빌드에서는 이 가지가 통째로 떨어져 나간다 — 픽스처 모듈이
 * 번들에 들어가지도 않는다. 「가드를 깜빡해서 운영에 목이 나가는」 사고가 구조적으로 불가능하다.
 *
 * <p>목을 걷어낼 때 고칠 곳도 여기 하나다. 화면은 이 함수의 존재를 모른다.
 */
async function stubbed(method: string, path: string, body: unknown): Promise<unknown | undefined> {
  if (!env.stubApi) return undefined;
  const { resolve, MISS } = await import('@/lib/api/devFixtures');
  const found = resolve(method, path, body);
  return found === MISS ? undefined : found;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  const token = readAccessToken();
  if (token !== null) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    // Content-Type 을 지정하지 않는다 — 브라우저가 boundary 를 붙여야 한다.
    body = options.body;
  }
  else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  return fetch(`${env.apiBaseUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body,
    signal: options.signal,
  });
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const stub = await stubbed(options.method ?? 'GET', path, options.body);
  if (stub !== undefined) return stub as T;

  let response = await send(path, options);

  if (response.status === 401 && (await refreshOnce())) {
    response = await send(path, options);
  }

  let envelope: Envelope<T> | null = null;
  try {
    envelope = (await response.json()) as Envelope<T>;
  }
  catch {
    // 봉투가 아닌 응답(프록시가 낸 502·HTML 오류 문서 등).
  }

  if (!response.ok || envelope === null || !envelope.ok) {
    if (response.status === 401) clearTokens();
    throw new ApiRequestError(response.status, envelope?.error ?? null);
  }

  return envelope.data as T;
}

/** 파일 내려받기. 봉투가 아니라 바이트가 오는 자리다. */
export async function apiFetchBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  let response = await send(path, options);
  if (response.status === 401 && (await refreshOnce())) {
    response = await send(path, options);
  }
  if (!response.ok) {
    if (response.status === 401) clearTokens();
    throw new ApiRequestError(response.status, null);
  }
  return response.blob();
}
